import { type CityListItem, type CityListQuery } from '@utazzo/shared';
import { UzBadge, UzStatusBadge } from '@utazzo/ui';
import React, { useMemo } from 'react';

import { listCities } from '../cities.api';
import { CityFormDrawer } from '../components/CityFormDrawer';
import {
  useBulkDeleteCities,
  useBulkSetCitiesStatus,
  useCitiesList,
  useCountries,
  useDeleteCity,
} from '../use-cities';

import { type DataGridColumn, type DataGridFilter } from '@/components/data-grid';
import { ListScreen, type ListScreenColumns } from '@/components/list-screen';
import { useCurrentUser } from '@/features/auth/use-auth';

const STATUS_OPTIONS = [
  { value: '', label: 'Active + inactive' },
  { value: 'active', label: 'Active only' },
  { value: 'inactive', label: 'Inactive only' },
] as const;

// Website visibility filter - see ADR-0031. Cities flagged for the website
// are exposed to the website CMS pickers (destinations, content authoring).
const WEBSITE_OPTIONS = [
  { value: '', label: 'Website: any' },
  { value: 'on', label: 'On website' },
  { value: 'off', label: 'Not on website' },
] as const;

// TBO link filter - ADR-0019 / 2026-06-24. Lets ops see how the
// post-seed `db:post-seed:tbo-match` script landed + surface the
// still-unmatched tail for a manual sweep.
const TBO_OPTIONS = [
  { value: '', label: 'TBO link: any' },
  { value: 'linked', label: 'Linked to TBO' },
  { value: 'unlinked', label: 'Not linked' },
] as const;

/** Server search only fires from this many characters. */
const MIN_SEARCH_LENGTH = 3;

export const CitiesListPage = (): React.JSX.Element => {
  const me = useCurrentUser();
  const canDelete = me?.role === 'ADMIN';
  const deleteCity = useDeleteCity();
  const bulkDeleteMut = useBulkDeleteCities();
  const bulkSetStatusMut = useBulkSetCitiesStatus();
  const countriesQuery = useCountries();

  const countryOptions = useMemo(
    () => [
      { value: '', label: 'All countries' },
      ...(countriesQuery.data ?? []).map((c) => ({ value: c.id, label: c.name })),
    ],
    [countriesQuery.data],
  );

  const filters: ReadonlyArray<DataGridFilter> = useMemo(
    () => [
      { id: 'country', label: 'Country', options: countryOptions, widthClass: 'w-56' },
      {
        id: 'status',
        label: 'Status',
        options: STATUS_OPTIONS as ReadonlyArray<{ value: string; label: string }>,
        widthClass: 'w-48',
      },
      {
        id: 'website',
        label: 'Website',
        options: WEBSITE_OPTIONS as ReadonlyArray<{ value: string; label: string }>,
        widthClass: 'w-44',
      },
      {
        id: 'tbo',
        label: 'TBO link',
        options: TBO_OPTIONS as ReadonlyArray<{ value: string; label: string }>,
        widthClass: 'w-44',
      },
    ],
    [countryOptions],
  );

  const columns: ListScreenColumns<CityListItem> = ({ state }) => {
    const cols: ReadonlyArray<DataGridColumn<CityListItem>> = [
      {
        id: 'serial',
        header: 'S.No',
        width: '64px',
        cell: (_row, index) => (
          <span className="text-zinc-500">{(state.page - 1) * state.pageSize + index + 1}.</span>
        ),
      },
      {
        id: 'name',
        header: 'City Name',
        sortable: true,
        cell: (row) => (
          <div className="flex flex-col">
            <span className="font-medium text-zinc-900">{row.name}</span>
            {(row.iataCode || row.customCityCode) && (
              <span className="text-xs text-zinc-500">
                {[row.iataCode, row.customCityCode].filter(Boolean).join(' · ')}
              </span>
            )}
          </div>
        ),
      },
      {
        id: 'tboCityId',
        header: 'TBO City Id',
        cell: (row) => <span className="font-mono text-zinc-600">{row.tboCityId ?? '-'}</span>,
      },
      {
        id: 'state',
        header: 'State Name',
        cell: (row) => <span>{row.state?.name ?? '-'}</span>,
      },
      {
        id: 'country',
        header: 'Country',
        cell: (row) => (
          <span>
            {row.country.name} ({row.country.isoCode.toUpperCase()})
          </span>
        ),
      },
      {
        id: 'website',
        header: 'Website',
        cell: (row) =>
          row.isWebsite ? (
            <UzBadge tone="success" pill>
              On
            </UzBadge>
          ) : (
            <span className="text-zinc-400">-</span>
          ),
      },
      {
        id: 'status',
        header: 'Status',
        cell: (row) => <UzStatusBadge status={row.isActive ? 'active' : 'inactive'} />,
      },
    ];
    return cols;
  };

  return (
    <ListScreen<CityListItem, CityListQuery>
      title="City Management"
      description="The city master used across itineraries, bookings, and the website catalog."
      useList={useCitiesList}
      defaultSort={{ by: 'name', dir: 'asc' }}
      minSearchLength={MIN_SEARCH_LENGTH}
      toQuery={(state, base) => {
        const country = state.filters.country;
        const status = state.filters.status;
        const website = state.filters.website;
        const tbo = state.filters.tbo;
        return {
          ...base,
          ...(country ? { countryId: country } : {}),
          ...(status === 'active' ? { isActive: true } : {}),
          ...(status === 'inactive' ? { isActive: false } : {}),
          ...(website === 'on' ? { isWebsite: true } : {}),
          ...(website === 'off' ? { isWebsite: false } : {}),
          ...(tbo === 'linked' ? { hasTboCityId: true } : {}),
          ...(tbo === 'unlinked' ? { hasTboCityId: false } : {}),
        };
      }}
      columns={columns}
      filters={filters}
      getRowId={(row) => row.id}
      createButton={{ label: 'Add new city' }}
      renderDrawer={({ open, mode, row, close }) => (
        <CityFormDrawer
          open={open}
          onOpenChange={(o) => {
            if (!o) close();
          }}
          mode={mode}
          city={row}
        />
      )}
      bulkActions={{
        delete: {
          onDelete: async (rows) => {
            await bulkDeleteMut.mutateAsync(rows.map((r) => r.id));
          },
          confirm: (rows) => ({
            title: `Delete ${rows.length} cit${rows.length === 1 ? 'y' : 'ies'}?`,
            description:
              'These cities will be soft-deleted and removed from lists. Records that already reference them are unaffected.',
          }),
        },
        setActive: {
          onSetActive: async (rows, isActive) =>
            bulkSetStatusMut.mutateAsync({ ids: rows.map((r) => r.id), isActive }),
          confirm: (rows, isActive) => ({
            title: `${isActive ? 'Activate' : 'Deactivate'} ${rows.length} cit${rows.length === 1 ? 'y' : 'ies'}?`,
            description: isActive
              ? 'They will become selectable again in itineraries, bookings, and pickers.'
              : 'They will be hidden from new selections but remain on historical records.',
          }),
        },
      }}
      export={{
        fetchPage: (q) => listCities(q),
        filename: 'cities',
        csv: [
          { header: 'City Name', value: (r) => r.name },
          { header: 'State Name', value: (r) => r.state?.name ?? '' },
          { header: 'TBO City Id', value: (r) => r.tboCityId ?? '' },
          { header: 'Country', value: (r) => r.country.name },
          { header: 'Website', value: (r) => (r.isWebsite ? 'Yes' : 'No') },
          { header: 'Status', value: (r) => (r.isActive ? 'Active' : 'Inactive') },
          { header: 'Created at', value: (r) => r.createdAt },
        ],
      }}
      rowActions={{
        edit: true,
        delete: {
          onDelete: (row) => deleteCity.mutateAsync(row.id),
          disabled: () => !canDelete,
          confirm: (row) => ({
            title: 'Delete this city?',
            description: `${row.name} will be soft-deleted and removed from lists. Records that already reference it are unaffected.`,
          }),
        },
      }}
      copy={{
        searchPlaceholder: 'Search by city, state, or code (min 3 characters)',
        emptyTitle: 'No cities match these filters',
        emptyDescription: 'Adjust the country or status filter, or clear the search box.',
        errorTitle: 'Failed to load cities',
        errorDescription: 'The server returned an error. Try refreshing or check your network.',
      }}
    />
  );
};
